import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { IdCard, User, Mail, Lock, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function Register() {
  const { register: registerUser, loginWithGoogle, googleEnabled } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', country_of_residence: '' });
  const [loading, setLoading] = useState(false);
  const [embassies, setEmbassies] = useState([]);

  useEffect(() => {
    api.get('/embassies').then(r => setEmbassies(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await registerUser(form.name, form.email, form.password, form.country_of_residence);
      toast.success('Compte cree avec succes');
      navigate('/citizen/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border border-border shadow-sm animate-fade-in">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-xl bg-[#007FFF] flex items-center justify-center mb-4">
            <IdCard className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Creer un compte</CardTitle>
          <CardDescription>Inscription gratuite - 0$</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {googleEnabled && (
            <>
              <Button variant="outline" className="w-full h-11" onClick={loginWithGoogle} data-testid="google-register-btn">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {t('auth.google')}
              </Button>
              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-muted-foreground">{t('auth.or')}</span>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="name" placeholder="Jean Mukeba" className="pl-10"
                  value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="register-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="votre@email.com" className="pl-10"
                  value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="register-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type="password" placeholder="Min. 6 caracteres" className="pl-10"
                  value={form.password} onChange={e => setForm({...form, password: e.target.value})} required data-testid="register-password" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('auth.country')}</Label>
              <Select value={form.country_of_residence} onValueChange={v => setForm({...form, country_of_residence: v})}>
                <SelectTrigger data-testid="register-country">
                  <SelectValue placeholder={t('common.choose')} />
                </SelectTrigger>
                <SelectContent>
                  {embassies.map(e => (
                    <SelectItem key={e.embassy_id} value={e.country}>{e.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full h-11 bg-[#007FFF] hover:bg-[#0066CC]" disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('auth.register_btn')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Deja inscrit ?{' '}
            <Link to="/login" className="text-[#007FFF] hover:underline font-medium" data-testid="goto-login">
              Se connecter
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
